import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate, type RouteObject } from "react-router-dom";
import { AppShell } from "@/components/layout/app-shell";
import { Permission, type PermissionKey } from "@/lib/permissions";
import { ProtectedRoute, PublicOnlyRoute } from "./protected-route";
import { RequirePermission } from "./require-permission";
import { RouteFallback, RouteErrorBoundary } from "./route-states";

/**
 * Every page is lazy so each screen is its own chunk. Keep it that way as
 * screens are added — the admin panel will grow well past what one bundle
 * should carry.
 */
const LoginPage = lazy(() => import("@/pages/auth/login-page"));
const DashboardPage = lazy(() => import("@/pages/dashboard/dashboard-page"));
const TaskInboxPage = lazy(() => import("@/pages/admin-user-tasks/task-inbox-page"));
const TaskDetailPage = lazy(() => import("@/pages/admin-user-tasks/task-detail-page"));
const TeamConsolePage = lazy(
  () => import("@/pages/admin-user-tasks/team/team-console-page"),
);
const BotAccountsPage = lazy(() => import("@/pages/bot-accounts/bot-accounts-page"));
const TranscriptionWordsPage = lazy(
  () => import("@/pages/transcription-words/transcription-words-page"),
);
const CallLogsPage = lazy(() => import("@/pages/call-logs/call-logs-page"));
const AgentCallDashboardPage = lazy(
  () => import("@/pages/call-dashboards/agent-call-dashboard-page"),
);
const CallsOverviewPage = lazy(
  () => import("@/pages/call-dashboards/calls-overview-page"),
);
const CallbacksPage = lazy(() => import("@/pages/callbacks/callbacks-page"));
const SecondVehicleLeadsPage = lazy(
  () => import("@/pages/second-vehicle-leads/second-vehicle-leads-page"),
);
const CallingNumbersPage = lazy(
  () => import("@/pages/calling-numbers/calling-numbers-page"),
);
const PermissionsPage = lazy(() => import("@/pages/admin-access/permissions-page"));
const PaymentsDigestPage = lazy(
  () => import("@/pages/payments-digest/payments-digest-page"),
);
const HourlyStatsPage = lazy(() => import("@/pages/hourly-stats/hourly-stats-page"));
const FailureAlertsPage = lazy(
  () => import("@/pages/failure-alerts/failure-alerts-page"),
);
const TulsiPage = lazy(() => import("@/pages/tulsi/tulsi-page"));
const TulsiGatheringPage = lazy(() => import("@/pages/tulsi-gathering/gathering-page"));
const TulsiInTrainingPage = lazy(
  () => import("@/pages/tulsi-in-training/in-training-page"),
);
const TulsiMissedTrainingPage = lazy(
  () => import("@/pages/tulsi-missed-training/missed-training-page"),
);
const AnjuPage = lazy(() => import("@/pages/anju/anju-page"));
const SheetalPage = lazy(() => import("@/pages/sheetal/sheetal-page"));
const SupportInsightsPage = lazy(
  () => import("@/pages/support-insights/support-insights-page"),
);
const AutomationPage = lazy(() => import("@/pages/automation/automation-page"));
const AutopilotPage = lazy(() => import("@/pages/autopilot/autopilot-page"));
const MatcherPage = lazy(() => import("@/pages/matcher/matcher-page"));
const CustomersWithoutChampPage = lazy(
  () => import("@/pages/customers-without-champ/customers-without-champ-page"),
);
const TodoPage = lazy(() => import("@/pages/todo/todo-page"));
const TodoFormPage = lazy(() => import("@/pages/todo/todo-form-page"));
const HomePopupsPage = lazy(() => import("@/pages/home-popups/home-popups-page"));
const JobListPage = lazy(() => import("@/pages/job-list/job-list-page"));
const JobsSearchPage = lazy(() => import("@/pages/service-jobs/jobs-search-page"));
const SubscriptionJobsPage = lazy(
  () => import("@/pages/service-jobs/subscription-jobs-page"),
);
const WhatsAppInboxPage = lazy(() => import("@/pages/whatsapp/whatsapp-inbox-page"));
const TicketsPage = lazy(() => import("@/pages/tickets/tickets-page"));
const TicketTeamPage = lazy(() => import("@/pages/tickets/team-page"));
const ReferralsPage = lazy(() => import("@/pages/referrals/referrals-page"));
const CampaignFormPage = lazy(() => import("@/pages/referrals/campaign-form-page"));
const CustomerPage = lazy(() => import("@/pages/customers/customer-page"));
const ChampPage = lazy(() => import("@/pages/champs/champ-page"));
const NotFoundPage = lazy(() => import("@/pages/not-found-page"));

/**
 * A screen behind its `*.view` permission. The sidebar hides screens an admin
 * cannot open; this covers a typed URL or a shared link.
 */
function guarded(permission: PermissionKey, page: ReactNode) {
  return (
    <RequirePermission permission={permission}>
      <Suspense fallback={<RouteFallback />}>{page}</Suspense>
    </RequirePermission>
  );
}

const routes: RouteObject[] = [
  {
    element: <PublicOnlyRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/login",
        element: (
          <Suspense fallback={<RouteFallback />}>
            <LoginPage />
          </Suspense>
        ),
      },
    ],
  },
  {
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          {
            path: "/dashboard",
            element: (
              <Suspense fallback={<RouteFallback />}>
                <DashboardPage />
              </Suspense>
            ),
          },
          {
            path: "/tasks",
            element: guarded(Permission.TASKS_VIEW, <TaskInboxPage />),
          },
          {
            /*
              A top-level path, NOT `/tasks/team`.

              Nesting it under `/tasks` would light two sidebar items at once:
              "My tasks" points at `/tasks` and a NavLink without `end` matches
              every descendant. `end` cannot fix it either — `/tasks` needs to
              stay active on `/tasks/:id` while not matching the console. A
              separate path sidesteps the whole thing, and reads better anyway:
              the console is its own destination, not a sub-page of your inbox.

              The API keeps `/admin-user-tasks/team/*` regardless; URL shapes on
              the two sides answer to different constraints.
            */
            path: "/team-tasks",
            element: guarded(Permission.TEAM_TASKS_VIEW, <TeamConsolePage />),
          },
          {
            path: "/job-list",
            element: guarded(Permission.JOB_LIST_VIEW, <JobListPage />),
          },
          {
            path: "/jobs",
            element: guarded(Permission.SERVICE_JOBS_VIEW, <JobsSearchPage />),
          },
          {
            path: "/customers-without-champ",
            element: guarded(Permission.MATCHER_BACKLOG, <CustomersWithoutChampPage />),
          },
          {
            path: "/jobs/subscriptions/:id",
            element: guarded(Permission.SERVICE_JOBS_VIEW, <SubscriptionJobsPage />),
          },
          {
            path: "/tasks/:id",
            element: guarded(Permission.TASKS_VIEW, <TaskDetailPage />),
          },
          {
            path: "/whatsapp",
            element: guarded(Permission.WHATSAPP_VIEW, <WhatsAppInboxPage />),
          },
          {
            path: "/tickets",
            element: guarded(Permission.TICKETS_VIEW, <TicketsPage />),
          },
          {
            path: "/tickets/team",
            element: guarded(Permission.TICKETS_MANAGE, <TicketTeamPage />),
          },
          {
            // Not a sidebar item: reached from a customer's name, the search or "Open as page".
            path: "/customers/:id",
            element: guarded(Permission.CUSTOMERS_VIEW, <CustomerPage />),
          },
          {
            // Not a sidebar item: reached from a champ's name, the search or "Open as page".
            path: "/champs/:id",
            element: guarded(Permission.CHAMPS_VIEW, <ChampPage />),
          },
          {
            path: "/calls",
            element: guarded(Permission.CALL_LOGS_VIEW, <CallLogsPage />),
          },
          {
            path: "/calls/overview",
            element: guarded(Permission.CALLS_OVERVIEW_VIEW, <CallsOverviewPage />),
          },
          {
            path: "/calls/agent-dashboard",
            element: guarded(
              Permission.AGENT_CALL_DASHBOARD_VIEW,
              <AgentCallDashboardPage />,
            ),
          },
          {
            path: "/calls/callbacks",
            element: guarded(Permission.CALLBACKS_VIEW, <CallbacksPage />),
          },
          {
            path: "/calls/second-vehicle-leads",
            element: guarded(
              Permission.SECOND_VEHICLE_LEADS_VIEW,
              <SecondVehicleLeadsPage />,
            ),
          },
          {
            path: "/calls/numbers",
            element: guarded(Permission.CALLING_NUMBERS_VIEW, <CallingNumbersPage />),
          },
          {
            path: "/reports/payments-digest",
            element: guarded(Permission.PAYMENTS_DIGEST_VIEW, <PaymentsDigestPage />),
          },
          {
            path: "/reports/hourly-stats",
            element: guarded(Permission.HOURLY_STATS_VIEW, <HourlyStatsPage />),
          },
          {
            path: "/reports/failure-alerts",
            element: guarded(Permission.FAILURE_ALERTS_VIEW, <FailureAlertsPage />),
          },
          {
            path: "/reports/tulsi",
            element: guarded(Permission.TULSI_VIEW, <TulsiPage />),
          },
          {
            path: "/reports/tulsi-gathering",
            element: guarded(Permission.TULSI_VIEW, <TulsiGatheringPage />),
          },
          {
            path: "/reports/tulsi-in-training",
            element: guarded(Permission.TULSI_VIEW, <TulsiInTrainingPage />),
          },
          {
            path: "/reports/tulsi-missed-training",
            element: guarded(Permission.TULSI_VIEW, <TulsiMissedTrainingPage />),
          },
          {
            path: "/reports/anju",
            element: guarded(Permission.ANJU_VIEW, <AnjuPage />),
          },
          {
            path: "/reports/sheetal",
            element: guarded(Permission.SHEETAL_VIEW, <SheetalPage />),
          },
          {
            path: "/reports/support-insights",
            element: guarded(Permission.SUPPORT_INSIGHTS_VIEW, <SupportInsightsPage />),
          },
          {
            path: "/referrals",
            element: guarded(Permission.REFERRALS_VIEW, <ReferralsPage />),
          },
          {
            path: "/referrals/new",
            element: guarded(Permission.REFERRALS_MANAGE, <CampaignFormPage />),
          },
          {
            path: "/referrals/:id/edit",
            element: guarded(Permission.REFERRALS_MANAGE, <CampaignFormPage />),
          },
          {
            path: "/todo",
            element: guarded(Permission.TODO_VIEW, <TodoPage />),
          },
          {
            path: "/todo/new",
            element: guarded(Permission.TODO_MANAGE, <TodoFormPage />),
          },
          {
            path: "/todo/:id/edit",
            element: guarded(Permission.TODO_MANAGE, <TodoFormPage />),
          },
          {
            path: "/settings/home-popups",
            element: guarded(Permission.HOME_POPUPS_VIEW, <HomePopupsPage />),
          },
          {
            path: "/settings/transcription-words",
            element: guarded(
              Permission.TRANSCRIPTION_WORDS_VIEW,
              <TranscriptionWordsPage />,
            ),
          },
          {
            path: "/settings/automation",
            element: guarded(Permission.AUTOMATION_VIEW, <AutomationPage />),
          },
          {
            path: "/settings/bots",
            element: guarded(Permission.BOTS_VIEW, <BotAccountsPage />),
          },
          {
            path: "/settings/autopilot",
            element: guarded(Permission.AUTOPILOT_RULES_EDIT, <AutopilotPage />),
          },
          {
            path: "/settings/matcher",
            element: guarded(Permission.MATCHER_VIEW, <MatcherPage />),
          },
          {
            path: "/settings/permissions",
            element: guarded(Permission.PERMISSIONS_VIEW, <PermissionsPage />),
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: (
      <Suspense fallback={<RouteFallback />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
];

export const router = createBrowserRouter(routes);
